import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import get from 'lodash/get';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { Dispatch, RootState } from 'src/config/rootStore';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { Button } from 'src/shared/components/Button';
import { useOnboardingGuard } from 'src/shared/hooks/useOnboardingGuard';
import { color, font, mediaQueries } from 'src/shared/styles/variables';
import clipboard from 'src/shared/svg/clipboard.svg';
import documentIcon from 'src/shared/svg/document.svg';
import personThreeCircles from 'src/shared/svg/person-three-circles.svg';

import { BrandWrapper } from '../components/BrandWrapper';
import { useStartCareRecommendationMutation } from '../documents/startCareRecommendationMutation.generated';

import CrisisSupportBanner from 'src/shared/components/CrisisSupportBanner';

const TileWrapper = styled.div({
    padding: 20,
    marginLeft: 15,
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '32.5%',
    border: `2px solid ${color.linkWaterLight}`,
    alignItems: 'center',
    borderRadius: 12,
    [mediaQueries.sm]: {
        marginTop: 15,
        width: '90%',
    },
});

const TileTitle = styled.h4(
    {
        textAlign: 'center',
        color: color.fiord,
        letterSpacing: 0,
    },
    font.heavy(font.size.medium),
);

const TileDescription = styled.p(
    {
        textAlign: 'center',
        color: color.blueBayoux,
        fontStyle: 'normal',
        letterSpacing: 0,
    },
    font.book(font.size.medium),
);

interface TileProps {
    description: string;
    title: string;
    img: string;
}

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled.i<StyledIconProps>(({ iconUrl }) => ({
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    width: '1.75rem',
    height: '1.75rem',
    backgroundSize: 'cover',
    backgroundColor: color.weepingWhaleBlue,
    backgroundPositionX: 'center',
}));

const IconWrapper = styled.div({
    padding: 15,
    backgroundColor: color.weepingWhaleBlue,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
});

const Tile: React.FC<TileProps> = ({ description, title, img }) => (
    <TileWrapper>
        <IconWrapper>
            <StyledIcon iconUrl={img} />
        </IconWrapper>
        <TileTitle>{title}</TileTitle>
        <TileDescription>{description}</TileDescription>
    </TileWrapper>
);

const Wrapper = styled.div({
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'inherit',
    textAlign: 'center',
    minWidth: 350,
});

const HeadingText = styled.h2(
    {
        color: color.pickledBluewood,
        marginTop: '1em',
        marginBottom: '1.2em',
    },
    font.black(font.size.extraLarge),
);

const TilesWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    maxWidth: 1050,
    marginBottom: 50,
    [mediaQueries.sm]: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
});

type Props = ReturnType<typeof mapStateToProps> & RouteComponentProps;

const WelcomeUserPage: React.FC<Props> = ({ history }) => {
    const shouldRender = useOnboardingGuard();
    const [loading, setLoading] = useState(false);
    const [startCareRecommendation] = useStartCareRecommendationMutation({
        client: appClient,
    });
    const dispatch: Dispatch = useDispatch();

    const { t } = useTranslation();

    const data = [
        {
            title: t('onboarding:welcome_user_page.tiles.tile_1.title'),
            description: t('onboarding:welcome_user_page.tiles.tile_1.description'),
            img: personThreeCircles,
        },
        {
            title: t('onboarding:welcome_user_page.tiles.tile_2.title'),
            description: t('onboarding:welcome_user_page.tiles.tile_2.description'),
            img: documentIcon,
        },
        {
            title: t('onboarding:welcome_user_page.tiles.tile_3.title'),
            description: t('onboarding:welcome_user_page.tiles.tile_3.description'),
            img: clipboard,
        },
    ];

    const submit = async () => {
        try {
            setLoading(true);
            // kick off a new care recommendation that will be updated through out the onboarding flow
            const res = await startCareRecommendation();
            const careUuid = get(res, 'data.startCareRecommendation.careRecommendation.uuid');
            if (!careUuid) {
                throw new Error('Did not receive care uuid');
            }

            await dispatch.user.fetchUserInfo();
            history.push('/onboarding-privacy');
        } catch (error) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);

            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to create a care recommendation uuid');

                Sentry.captureException(error);
            });
        } finally {
            setLoading(false);
        }
    };

    return shouldRender ? (
        <BrandWrapper>
            <Wrapper>
                <HeadingText>{t('onboarding:welcome_user_page.title')}</HeadingText>
                <TilesWrapper>
                    {data.map(tileData => (
                        <Tile
                            description={tileData.description}
                            title={tileData.title}
                            key={tileData.description}
                            img={tileData.img}
                        />
                    ))}
                </TilesWrapper>

                <Button
                    loading={loading}
                    onClick={submit}
                    type="accept"
                    width="normal"
                    noHorizontalPadding
                >
                    {t('onboarding:welcome_user_page.lets_begin')}
                </Button>

                <CrisisSupportBanner textOnly />
            </Wrapper>
        </BrandWrapper>
    ) : null;
};

const mapStateToProps = (state: RootState) => ({
    user: getUserInformation(state),
});

export default connect(mapStateToProps)(WelcomeUserPage);
